/* ============================================================
   JUCE 3.0 - juce-capi.js
   THE BROWSER HALF OF SERVER-SIDE CONVERSIONS.

   It does exactly three things and nothing else:

     1. MINTS THE EVENT ID. Meta collapses a pixel event and a Conversions API
        event into one when they share the event name and the event id. The id
        has to come from one place or the two sides cannot agree, and the
        browser is that place - it is the only side that sees the moment
        happen. The id is announced on a `juce:capi` document event before it
        is sent, so whatever ends up owning the pixel can pass the SAME string
        as fbq's eventID and the pair collapses.

     2. READS WHAT ONLY A BROWSER CAN SEE. _fbp and _fbc (Meta's own cookies,
        written by whichever pixel is installed), the _ga client and session
        ids, and a gclid off the URL. These are what make a match land for a
        shopper who never signed in, and none of them exists on the server.

     3. TELLS THE SERVER. One small POST to /a/x. The server decides
        everything else: which destinations are on, whether this shopper
        consented, what the payload looks like, and whether it is a rehearsal.
        Nothing here talks to Meta or Google directly, which is the whole
        point of a server-side integration.

   THE PURCHASE IS NOT HERE AND MUST NEVER BE ADDED.

   The purchase happens on Shopify's checkout. This theme does not own that
   page and cannot put a script on it, and the order-status page is not the
   place either: it is blocked for a large share of shoppers and missed
   entirely by everybody who closes the tab when the payment clears. The
   purchase event is built on the server from the mirrored orders table, where
   "paid" is a fact rather than a page load - see
   jennie-buildout/server/src/integrations/conversions.ts (conversionsSweep).
   The server REFUSES a purchase arriving on /a/x for the same reason.

   If a purchase ever does need reporting from a browser, it belongs in a
   Shopify Custom Pixel using the same id this server derives,
   "order-<shopify order id>", or Meta counts the order twice.

   THREE RULES, the same three the gate and the tracker keep.

   1. IT NEVER BREAKS THE SHOP. Every path swallows its own errors. Nothing
      here is worth a shopper not being able to buy perfume.
   2. SILENCE IS NOT A YES. Nothing is read and nothing is sent until
      juce-consent.js has answered, and then only for the categories that were
      actually granted. A shopper who declined has this file do nothing at all
      for the rest of the visit.
   3. IT IS PASSIVE, SO IT DEFERS. Listeners bind at once - they observe
      controls that other code owns, and a listener that has not bound yet is
      a conversion that never happened - but every read, every cookie parse
      and every network call happens off the critical path.
      steering/12-performance-playbook.md section 3.2.
   ============================================================ */

const conf = (window.theme && window.theme.capi) || null;
if (conf && conf.endpoint) {
  try { start(); } catch (err) { /* rule 1 */ }
}

function start() {
  const ENDPOINT = String(conf.endpoint).replace(/\/+$/, '') + '/a/x';
  /* Which moments this theme reports. Deliberately short: view, add, and the
     step into checkout. page_view and search are off on the server by default
     too - they are volume without signal for a shop this size - and purchase
     is the server's, permanently. The server gates every one of these again
     on its own dials, so this list can only ever be narrower than what is
     switched on, never wider. */
  const WANT = Array.isArray(conf.events) && conf.events.length
    ? conf.events
    : ['view_item', 'add_to_cart', 'begin_checkout'];

  /* THE GATE. `grants` stays empty until juce-consent.js has answered, and
     every send checks it - so an event that happens during the first second
     of a page, before the answer arrives, is simply not sent rather than
     queued for later. That is the right trade here and it is different from
     LOOM's: LOOM buffers because a journey with a hole in it is worthless,
     while a single missing ViewContent costs nothing and a buffered one would
     mean holding a shopper's browsing in memory before they agreed to it. */
  let grants = [];
  let ready = false;

  function may() {
    /* Either category opens the door, because the two destinations sit behind
       different ones: GA4 needs analytics, Meta and Google Ads need
       marketing. Which of them actually receives the event is the server's
       decision (integrations/conversions.ts) and it is made again there. */
    return ready && (grants.indexOf('marketing') !== -1 || grants.indexOf('analytics') !== -1);
  }

  document.addEventListener('juce:consent', (e) => {
    try {
      grants = (e.detail && e.detail.grants) ? e.detail.grants.slice() : [];
      ready = true;
      /* A shopper who agrees on the banner, on the page they are already
         standing on, should still have that page counted. The landing event
         is re-tried once the answer lands, and only once. */
      if (may() && !landed) landing();
    } catch (err) { /* rule 1 */ }
  });

  /* ---- what only a browser can see ---------------------------------- */

  function cookie(name) {
    try {
      const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (err) { return null; }
  }

  /* _ga is "GA1.1.<client>.<timestamp>" and GA4 wants the last two fields
     joined - "1234567890.1755781200" - not the whole cookie. Sending the
     whole thing makes every event a brand new user. */
  function gaClientId() {
    const raw = cookie('_ga');
    if (!raw) return null;
    const bits = raw.split('.');
    return bits.length >= 4 ? bits.slice(-2).join('.') : null;
  }

  /* The session id lives in a per-property cookie whose name carries the
     container id (_ga_YNVZNQGSWE is the GA4 property; a second _ga_ cookie
     on this shop belongs to the Merchant Center tag, not to another GA4
     property). Any of them is better than none: the value is
     "GS1.1.<session>.<n>....". */
  function gaSessionId() {
    try {
      const m = document.cookie.match(/_ga_[A-Z0-9]+=GS\d\.\d\.(\d+)/);
      return m ? m[1] : null;
    } catch (err) { return null; }
  }

  /* _fbc is normally written by the pixel when somebody arrives on an fbclid
     link. If the pixel is slow, blocked, or has not been given consent yet,
     the click id is still sitting in the URL - and Meta's own documented
     format for building the value is version.subdomainIndex.timestamp.fbclid. */
  function fbc() {
    const stored = cookie('_fbc');
    if (stored) return stored;
    try {
      const id = new URLSearchParams(location.search).get('fbclid');
      return id ? 'fb.1.' + Date.now() + '.' + id : null;
    } catch (err) { return null; }
  }

  function gclid() {
    try { return new URLSearchParams(location.search).get('gclid'); }
    catch (err) { return null; }
  }

  /* THE ADDRESS NEVER LEAVES THIS BROWSER. Both destinations hash a
     lowercased, trimmed email exactly the same way, so the digest is computed
     here and the address is not sent. Same trade juce-loom.js already makes
     for a newsletter signup, and it means a request log, a proxy and a queue
     row can never hold a customer's email. */
  let emailHash = null;
  async function hashEmail() {
    try {
      const c = window.theme && window.theme.customer;
      const addr = c && c.signed_in && c.email ? String(c.email).trim().toLowerCase() : '';
      if (!addr || !window.crypto || !crypto.subtle) return;
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(addr));
      emailHash = Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (err) { /* rule 1 - an unhashed address is simply not sent */ }
  }

  /* ---- money ---------------------------------------------------------
     Read from the DOM the theme already painted rather than fetched, because
     a fetch on the way into checkout loses its race with the navigation.
     Cents where an element carries an exact integer; the printed price
     otherwise ("R 745" or "R 745.50", with a non-breaking space). A price we
     cannot read is left OFF the event: Meta and Google both bid on value, and
     a conversion valued at zero is worse than one with no value at all. */
  function priceFrom(el) {
    if (!el) return null;
    const cents = el.getAttribute && el.getAttribute('data-total-cents');
    if (cents != null && cents !== '') {
      const n = Number(cents);
      if (isFinite(n)) return Math.round(n) / 100;
    }
    const text = String(el.textContent || '').replace(/[^0-9.]/g, '');
    const v = parseFloat(text);
    return isFinite(v) && v > 0 ? v : null;
  }

  function handle() {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function variantId(scope) {
    try {
      const picked = (scope && (scope.querySelector('input[name="id"]:checked')
        || scope.querySelector('[data-variant-input]'))) || null;
      if (picked && picked.value) return String(picked.value);
      return new URLSearchParams(location.search).get('variant');
    } catch (err) { return null; }
  }

  /* ---- sending ------------------------------------------------------- */

  /* text/plain keeps this a "simple" request, so there is never a CORS
     preflight in front of a conversion - the same reason LOOM sends its
     batches that way. credentials are included because juce_vid is HttpOnly
     and it is the id the shopper's consent record hangs on; without it the
     server has nobody to check and refuses the event, which is correct. */
  function post(payload) {
    const body = JSON.stringify(payload);
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body,
        credentials: 'include',
        /* keepalive so an event fired on the way into checkout still goes.
           sendBeacon cannot be used here: it does not carry credentials to a
           cross-origin endpoint, and without the cookie there is no consent
           subject. */
        keepalive: true,
        mode: 'cors',
      }).catch(() => { /* rule 1 */ });
    } catch (err) { /* rule 1 */ }
  }

  function send(name, extra) {
    try {
      if (!may() || WANT.indexOf(name) === -1) return;
      /* THE ID THAT MAKES DEDUPLICATION POSSIBLE. randomUUID everywhere it
         exists; the fallback is only for very old browsers and is still
         unique enough for a 48-hour pairing window. */
      const id = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'e' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);

      const payload = Object.assign({
        ev: name,
        id: id,
        u: location.href.slice(0, 500),
        r: document.referrer || undefined,
        c: 'ZAR',
        eh: emailHash || undefined,
        fbp: cookie('_fbp') || undefined,
        fbc: fbc() || undefined,
        gcid: gaClientId() || undefined,
        gsid: gaSessionId() || undefined,
        gclid: gclid() || undefined,
      }, extra || {});

      /* ANNOUNCED BEFORE IT IS SENT, so whatever owns the browser pixel can
         use the same id. Today the Meta pixel on this shop is installed as a
         Shopify app and cannot be handed one - see the "Dedup, honestly"
         section of jennie-buildout/docs/CONVERSIONS.md - so this is a seam
         waiting for the pixel to become ours rather than a live wire. When it
         does:  fbq('track', 'ViewContent', props, { eventID: detail.id }) */
      try {
        document.dispatchEvent(new CustomEvent('juce:capi', {
          detail: { event: name, id: id, payload: payload }
        }));
      } catch (err) { /* rule 1 */ }

      post(payload);
    } catch (err) { /* rule 1 */ }
  }

  /* ---- the moments ---------------------------------------------------- */

  let landed = false;

  function landing() {
    if (landed) return;
    const h = handle();
    if (!h) { landed = true; return; }
    landed = true;
    const vid = variantId(document);
    send('view_item', {
      v: priceFrom(document.querySelector('[data-pdp-price]')) || undefined,
      i: [{ id: vid || h, q: 1, p: priceFrom(document.querySelector('[data-pdp-price]')) || 0 }],
    });
  }

  /* An add SUBMITTED, on the same hook LOOM uses - [data-product-form] is the
     seam every add-to-cart form in this theme carries, so this survives a
     redesign of the buy column. Capture phase because theme.js intercepts the
     submit and fetches itself. */
  document.addEventListener('submit', (e) => {
    try {
      const form = e.target instanceof Element ? e.target.closest('[data-product-form]') : null;
      if (!form) return;
      const vid = variantId(form);
      const price = priceFrom(form.querySelector('[data-pdp-price]'))
        || priceFrom(document.querySelector('[data-pdp-price]'));
      const qty = Number((form.querySelector('input[name="quantity"]') || {}).value) || 1;
      send('add_to_cart', {
        v: price ? Math.round(price * qty * 100) / 100 : undefined,
        i: [{ id: vid || handle() || '', q: qty, p: price || 0 }],
      });
    } catch (err) { /* rule 1 */ }
  }, true);

  /* The step into checkout. name="checkout" is the one hook every route to
     the checkout in this theme shares - the drawer button, the cart page
     button and the two styled wallet bridges all carry it - so this is one
     listener rather than a list of selectors that goes stale. */
  document.addEventListener('click', (e) => {
    try {
      const el = e.target instanceof Element
        ? e.target.closest('[name="checkout"], [data-loom-cta="cart-checkout"]') : null;
      if (!el) return;
      send('begin_checkout', {
        v: priceFrom(document.querySelector('[data-total-cents]'))
          || priceFrom(document.querySelector('[data-cart-total]'))
          || undefined,
      });
    } catch (err) { /* rule 1 */ }
  }, true);

  /* ---- boot -----------------------------------------------------------
     Listeners are bound above, synchronously, because a listener that has not
     bound yet is a tap that reports nothing. Everything else - the hash, the
     first read of the consent record, the landing event - waits for idle, and
     for a prerendered page it waits until the tab is actually activated so a
     hover-prerender is never counted as a product view. Same rule LOOM
     follows. */
  function arm() {
    void (async () => {
      try {
        await hashEmail();
        const gate = window.juceConsent;
        if (gate && gate.ready && typeof gate.ready.then === 'function') {
          const state = await gate.ready;
          grants = (state && state.grants) ? state.grants.slice() : [];
        } else if (gate && gate.grants) {
          grants = gate.grants.slice();
        }
        ready = true;
        if (may()) landing();
      } catch (err) { /* rule 1 */ }
    })();
  }

  if (document.prerendering) {
    document.addEventListener('prerenderingchange', () => idle(arm), { once: true });
  } else {
    idle(arm);
  }

  function idle(fn) {
    if (window.requestIdleCallback) requestIdleCallback(fn, { timeout: 1000 });
    else setTimeout(fn, 200);
  }
}
